import { useState, useEffect, useCallback } from 'react';
import { useDebounce } from 'use-debounce';

import { searchPatients } from 'services/api';
import { PatientRecord } from 'record/patient.record';
import { OptionType } from 'components/ui/form-fields';

export type PractitionerRecord = {
  name: string | null;
  id: number | null;
};

export const rosterSortByOptions: OptionType[] = [
  { value: 0, label: 'Added: Oldest Patient', column: 'p.created' },
  { value: 1, label: 'Added: Newest Patient', column: 'p.created' },
  { value: 2, label: 'Age: Oldest Patient', column: 'p.datetime_of_birth' },
  { value: 3, label: 'Age: Youngest Patient', column: 'p.datetime_of_birth' },
  { value: 4, label: 'Last Assessment: Oldest', column: 'ar.created' },
  { value: 5, label: 'Last Assessment: Newest', column: 'ar.created' },
  { value: 6, label: 'Patient ID', column: 'p.ref_id' },
];

const SEARCH_DEBOUNCE_DELAY = 750;

export const usePatientsData = (isMyPatients: boolean) => {
  const [activePatients, setActivePatients] = useState<PatientRecord[]>([]);
  const [activePatientsGrouped, setActivePatientsGrouped] = useState<Record<string, PatientRecord[]> | null>(null);
  const [notActivePatients, setNotActivePatients] = useState<PatientRecord[] | null>(null);
  const [practitioners, setPractitioners] = useState<PractitionerRecord[]>([]);
  const [sortBy, setSortBy] = useState<OptionType['value']>(rosterSortByOptions[1].value);
  const [searchValue, setSearchValue] = useState<string>('');
  const [debouncedSearchValue] = useDebounce(searchValue, SEARCH_DEBOUNCE_DELAY);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const groupByAssignedUserId = (records: PatientRecord[]): Record<string, PatientRecord[]> => {
    const practitionerRecords: PractitionerRecord[] = [];
    const otherRecords: PatientRecord[] = [];

    const groupedRecordsUnsorted = records.reduce((groupedRecords, record) => {
      const key = record.assignedUserId;
      if (key !== null) {
        if (!groupedRecords[key]) {
          groupedRecords[key] = [];
          practitionerRecords.push({
            name: record.assignedUserName,
            id: record.assignedUserId,
          });
        }
        groupedRecords[key].push(record);
      } else {
        otherRecords.push(record);
      }
      return groupedRecords;
    }, {} as Record<string, PatientRecord[]>);

    const groupedRecords = Object.fromEntries(
      Object.entries(groupedRecordsUnsorted).sort(([keyA], [keyB]) => keyA.localeCompare(keyB)),
    );
    // Append other records to the end of the grouped records
    if (otherRecords.length) {
      groupedRecords['other'] = otherRecords;
    }

    setPractitioners(practitionerRecords);
    return groupedRecords;
  };

  const activePatientsIsEmpty = !activePatients || Object.keys(activePatients).length === 0;
  const arePatientsEmpty = activePatientsIsEmpty && !notActivePatients?.length;

  const fetchPatients = useCallback(
    async (isActive: boolean, isShowMyPatients: boolean = false) => {
      setActivePatients([]);
      setNotActivePatients(null);
      let filters: any = { IsActive: isActive };

      if (isShowMyPatients) {
        filters.MyPatients = isShowMyPatients;
      }
      try {
        setSubmitting(true);
        const { data } = await searchPatients({
          search_value: debouncedSearchValue ? debouncedSearchValue : undefined,
          filters,
          includes: ['assigned_user_info', 'overall_score_from_two_last_assessments'],
          sort_criteria: {
            column: rosterSortByOptions.find(option => option.value === sortBy)!.column,
            option: (sortBy as number) % 2,
          },
        });
        const patients = data.map((record: any) => new PatientRecord(record));
        if (isActive) {
          setActivePatientsGrouped(groupByAssignedUserId(patients));
          setActivePatients(patients);
        } else {
          setNotActivePatients(patients);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setSubmitting(false);
      }
    },
    [debouncedSearchValue, sortBy],
  );

  const fetch = useCallback(
    async (myPatients?: boolean) => {
      await Promise.all([fetchPatients(true, myPatients), fetchPatients(false, myPatients)]);
    },
    [fetchPatients],
  );

  useEffect(() => {
    fetch(isMyPatients);
  }, [debouncedSearchValue, sortBy, fetch, isMyPatients]);

  return {
    activePatients,
    activePatientsGrouped,
    notActivePatients,
    practitioners,
    fetch,
    searchValue,
    setSearchValue,
    setSortBy,
    sortBy,
    arePatientsEmpty,
    submitting,
  };
};
